import { useCallbackProxy, usePan, useThrottledCallback } from '@timelyapp/react';
import type { StateRefObject } from 'hooks/useStateRef';
import { clamp } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { scrollbarSize } from 'utils/dom-helpers';
import styles from './GanttChart.module.scss';
import type { GanttInteractionMode, ViewportState } from './gantt-internal-types';
import { useWeekdayBackground } from './gantt-weekday-background';
import { ZOOM_LEVELS } from './gantt-zoom';

export interface ScrollAreaApi {
  scrollToColumn: (column: number) => void;
  scrollToTop: (scrollTop?: number) => void;
  zoomToLevel: (level: number) => void;
}
interface Props {
  children: React.ReactNode;
  height: number;
  mode: GanttInteractionMode;
  rowStartDate: string;
  scrollAreaApiRef: (api: ScrollAreaApi) => void;
  todayLine: React.ReactNode;
  totalScrollHeight: number;
  viewportStateRef: StateRefObject<ViewportState>;
  width: number;
  defaultWeekDays: Array<string>; // MON, TUE, WED, THU, FRI, SAT, SUN
}

export const INITIAL_DAYS_TO_SCROLL = 365;
export const GanttScrollArea = (props: Props) => {
  const {
    children,
    height,
    mode,
    rowStartDate,
    scrollAreaApiRef,
    todayLine,
    totalScrollHeight,
    viewportStateRef,
    width,
    defaultWeekDays,
  } = props;

  const [scrollAreaRef, setScrollAreaRef] = useState<HTMLDivElement | null>(null);
  const { getState: getViewportState, setState: setViewportState } = viewportStateRef;
  const setViewport = useCallback(
    (state: ViewportState) => {
      setViewportState(state);

      // Avoid overwriting scrollLeft/Top unnecessarily, as it can stop smooth scrolling
      if (scrollAreaRef!.scrollLeft !== state.scrollLeft) {
        scrollAreaRef!.scrollLeft = state.scrollLeft;
      }
      if (scrollAreaRef!.scrollTop !== state.scrollTop) {
        scrollAreaRef!.scrollTop = state.scrollTop;
      }
    },
    [scrollAreaRef, setViewportState]
  );
  const scrollToColumnTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const lastEventSource = useRef<'scroll' | 'wheel'>('scroll');

  const handleScroll = useThrottledCallback(() => {
    if (lastEventSource.current === 'wheel') {
      lastEventSource.current = 'scroll';
      // we already handled the scroll in the wheel event
      return;
    }

    const { scrollTop, scrollLeft } = getViewportState();
    const { scrollLeft: newScrollLeft, scrollTop: newScrollTop } = scrollAreaRef!;
    if (newScrollTop === scrollTop && newScrollLeft === scrollLeft) {
      return;
    }

    setViewport(
      adjustInfiniteScroll(
        {
          ...getViewportState(),
          scrollTop: newScrollTop,
          scrollLeft: newScrollLeft,
        },
        width,
        // Avoid adjusting size of scroll area while smooth scrolling
        scrollToColumnTimeoutRef.current != null
      )
    );
  }, 50);

  const handleZoom = useCallback(
    (newZoomLevel: number, e?: WheelEvent) => {
      if (!scrollAreaRef) {
        return;
      }
      const { zoomLevel, scrollLeft } = getViewportState();

      let clientX = 0;
      if (e) {
        clientX = e.clientX - scrollAreaRef.getBoundingClientRect().x;
      } else {
        clientX = width / 2;
      }
      const hoveredColumn = (scrollLeft + clientX) / ZOOM_LEVELS[zoomLevel].columnWidth;

      const newZoom = ZOOM_LEVELS[newZoomLevel];
      const newScrollLeft = Math.floor(hoveredColumn * newZoom.columnWidth - clientX);

      setViewport(
        adjustInfiniteScroll(
          {
            ...getViewportState(),
            zoomLevel: newZoomLevel,
            scrollLeft: newScrollLeft,
          },
          width,
          // Avoid adjusting size of scroll area while smooth scrolling
          scrollToColumnTimeoutRef.current != null
        )
      );
    },
    [scrollAreaRef, setViewport, getViewportState, width]
  );

  /**
   * Using callback proxy here so that the outer function reference never changes,
   * as we are manually adding the wheel event below
   */
  const handleWheel = useCallbackProxy((e: WheelEvent) => {
    e.preventDefault();
    if (!scrollAreaRef) {
      return;
    }
    if (!(e.metaKey || e.ctrlKey)) {
      lastEventSource.current = 'wheel';
      const newScrollLeft = scrollAreaRef.scrollLeft + e.deltaX;
      const newScrollTop = clamp(
        scrollAreaRef.scrollTop + e.deltaY,
        0,
        totalScrollHeight - height + 10 // ???
      );
      setViewport(
        adjustInfiniteScroll(
          { ...getViewportState(), scrollLeft: newScrollLeft, scrollTop: newScrollTop },
          width,
          scrollToColumnTimeoutRef.current != null
        )
      );
      return;
    }

    const { zoomLevel } = getViewportState();
    const deltaZoomLevel = e.deltaY < 0 ? -1 : 1;
    const newZoomLevel = clamp(zoomLevel + deltaZoomLevel, 0, ZOOM_LEVELS.length - 1);
    if (newZoomLevel === zoomLevel) {
      return;
    }

    handleZoom(newZoomLevel, e);
  });

  useEffect(() => {
    if (scrollAreaRef != null) {
      // Adding wheel event the old way since react onWheel does not support passive=false
      scrollAreaRef.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        scrollAreaRef.removeEventListener('wheel', handleWheel);
      };
    }
  }, [scrollAreaRef, handleWheel]);

  // Panning
  const handlePan = useCallback(
    (pan: { left: number; top: number }) => {
      // These changes will get picked up by handleScroll above
      scrollAreaRef!.scrollTop += pan.top;
      scrollAreaRef!.scrollLeft += pan.left;
    },
    [scrollAreaRef]
  );
  const isPanMode = mode === 'pan';
  const panRef = usePan(handlePan, !isPanMode);

  const scrollToColumn = useCallbackProxy((columnIndex: number) => {
    const { zoomLevel } = getViewportState();
    const zoom = ZOOM_LEVELS[zoomLevel];
    const scrollLeft = columnIndex * zoom.columnWidth - width / 2;

    scrollAreaRef?.scroll({
      left: scrollLeft,
    });
    clearTimeout(scrollToColumnTimeoutRef.current!);
    scrollToColumnTimeoutRef.current = setTimeout(() => {
      scrollToColumnTimeoutRef.current = null;
      setViewport(adjustInfiniteScroll(getViewportState(), width, false));
    }, 1000);
  });
  const zoomToLevel = useCallbackProxy((level: number) => {
    // TODO: investigate why this timeout is necessary
    setTimeout(() => handleZoom(level), 0);
  });
  const scrollToTop = useCallbackProxy((scrollTop = 0) => {
    if (scrollAreaRef) {
      scrollAreaRef.scrollTop = scrollTop;
    }
  });
  const api = useMemo(
    () => ({ scrollToColumn, zoomToLevel, scrollToTop }),
    [scrollToColumn, zoomToLevel, scrollToTop]
  );
  useEffect(() => scrollAreaApiRef(api), [scrollAreaApiRef, api]);

  const viewportState = getViewportState();

  // Scroll to today on mount
  useEffect(
    () => {
      if (scrollAreaRef != null) {
        scrollAreaRef!.scrollLeft = viewportState.scrollLeft;
      }
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [scrollAreaRef]
  );

  const weekdayBackground = useWeekdayBackground(
    viewportState.zoomLevel,
    rowStartDate,
    defaultWeekDays
  );
  const adjustedScrollHeight = Math.max(totalScrollHeight, height - scrollbarSize());
  const zoom = ZOOM_LEVELS[viewportState.zoomLevel];
  const columnCount = viewportState.columnEnd - viewportState.columnStart;
  const totalScrollWidth = zoom.columnWidth * columnCount;

  return (
    <div
      ref={setScrollAreaRef}
      className={styles.mainScrollableContainer}
      onScroll={handleScroll}
      style={{
        height,
        width,
      }}
    >
      <div
        ref={isPanMode ? undefined : panRef}
        className={styles.mainScollableContent}
        style={{
          width: totalScrollWidth,
          height: adjustedScrollHeight,
          ...weekdayBackground,
        }}
      >
        {todayLine}
        {children}
      </div>
      {isPanMode && (
        <div
          ref={panRef}
          className={styles.panOverlay}
          style={{ width: totalScrollWidth, height: adjustedScrollHeight }}
        />
      )}
    </div>
  );
};

// TODO: split this up into smaller functions, and add tests
const adjustInfiniteScroll = (
  state: ViewportState,
  viewportWidth: number,
  animatingScroll: boolean
): ViewportState => {
  if (animatingScroll) {
    return state;
  }
  const { scrollLeft, columnStart, columnEnd, zoomLevel } = state;
  const zoom = ZOOM_LEVELS[zoomLevel];
  const columnCount = columnEnd - columnStart;
  const totalScrollWidth = zoom.columnWidth * columnCount;

  const THRESHOLD_COLUMNS = 100;
  const MARGIN_WIDTH = THRESHOLD_COLUMNS * zoom.columnWidth;

  const runningOutOfDaysToLeft = scrollLeft < MARGIN_WIDTH;
  const runningOutOfDaysRight = scrollLeft + viewportWidth > totalScrollWidth - MARGIN_WIDTH;

  const currentColumnLeft = Math.floor(scrollLeft / zoom.columnWidth) + columnStart;
  const columnsToShrinkLeft = clamp(
    currentColumnLeft - columnStart - THRESHOLD_COLUMNS * 2,
    0,
    -INITIAL_DAYS_TO_SCROLL - columnStart
  );
  const currentColumnRight =
    Math.ceil((scrollLeft + viewportWidth) / zoom.columnWidth) + columnStart;
  const columnsToShrinkRight = clamp(
    columnEnd - currentColumnRight - THRESHOLD_COLUMNS * 2,
    0,
    columnEnd - INITIAL_DAYS_TO_SCROLL
  );

  if (runningOutOfDaysToLeft) {
    const newScrollLeft = state.scrollLeft + MARGIN_WIDTH;
    return { ...state, scrollLeft: newScrollLeft, columnStart: columnStart - THRESHOLD_COLUMNS };
  } else if (columnsToShrinkLeft > 0) {
    const newScrollLeft = state.scrollLeft - columnsToShrinkLeft * zoom.columnWidth;
    return { ...state, columnStart: columnStart + columnsToShrinkLeft, scrollLeft: newScrollLeft };
  }
  if (runningOutOfDaysRight) {
    return { ...state, columnEnd: columnEnd + THRESHOLD_COLUMNS };
  } else if (columnsToShrinkRight > 0) {
    return { ...state, columnEnd: columnEnd - columnsToShrinkRight };
  }
  return state;
};
